import React, { useState, useEffect } from 'react';
import { apiCall } from '../services/api';

interface BucketUsageDetail {
  permissionKey: string;
  bucketName: string;
  folderPath: string | null;
  displayName: string;
  monthlyDataUsage: number;
  monthlyDownloadSize: number;
  monthlyUploadSize: number;
  monthlyDataTransferCap: number;
  capInBytes: number;
  remainingBytes: number;
  usagePercentage: number;
}

interface UserDataUsageResponse {
  userId: string;
  currentMonth: string;
  globalStats: {
    monthlyDataUsage: number;
    monthlyDownloadSize: number;
    monthlyUploadSize: number;
  };
  bucketUsageDetails: BucketUsageDetail[];
}

interface UserDataUsageModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
}

const UserDataUsageModal: React.FC<UserDataUsageModalProps> = ({
  isOpen,
  onClose,
  userId
}) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<UserDataUsageResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && userId) {
      loadUserDataUsage();
    }
  }, [isOpen, userId]);

  const loadUserDataUsage = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiCall(`/admin/user-data-usage/${encodeURIComponent(userId)}`);
      setData(response);
    } catch (err: any) {
      setError(err.message || 'Failed to load user data usage');
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    if (bytes < 0) return '∞'; // Unlimited
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const getUsageColor = (percentage: number): string => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 70) return 'bg-amber-500';
    return 'bg-green-500';
  };

  const getUsageTextColor = (percentage: number): string => {
    if (percentage >= 90) return 'text-red-600';
    if (percentage >= 70) return 'text-amber-600';
    return 'text-green-600';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Data Usage Details</h3>
                <p className="text-sm text-gray-500">User: {userId}</p>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-white/50 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
              <span className="ml-3 text-gray-600">Loading usage data...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-red-600">{error}</p>
            </div>
          ) : data ? (
            <>
              {/* Global Stats Summary */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <h4 className="text-sm font-medium text-gray-500 mb-3">Total Usage (All Buckets)</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">{formatBytes(data.globalStats.monthlyDataUsage)}</p>
                    <p className="text-xs text-gray-500">Total</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">{formatBytes(data.globalStats.monthlyDownloadSize)}</p>
                    <p className="text-xs text-gray-500">↓ Downloads</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">{formatBytes(data.globalStats.monthlyUploadSize)}</p>
                    <p className="text-xs text-gray-500">↑ Uploads</p>
                  </div>
                </div>
              </div>

              {/* Per-Bucket Usage Table */}
              <h4 className="text-sm font-medium text-gray-700 mb-3">Usage by Bucket/Folder</h4>
              {data.bucketUsageDetails.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No bucket permissions found for this user.
                </div>
              ) : (
                <div className="space-y-3">
                  {data.bucketUsageDetails.map((bucket) => (
                    <div key={bucket.permissionKey} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${bucket.folderPath ? 'bg-blue-100' : 'bg-gray-100'}`}>
                            <svg className={`w-4 h-4 ${bucket.folderPath ? 'text-blue-600' : 'text-gray-600'}`} fill="currentColor" viewBox="0 0 20 20">
                              {bucket.folderPath ? (
                                <path fillRule="evenodd" d="M2 6a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1H8a3 3 0 00-3 3v1.5a1.5 1.5 0 01-3 0V6z" clipRule="evenodd" />
                              ) : (
                                <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6z" />
                              )}
                            </svg>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{bucket.bucketName}</p>
                            {bucket.folderPath && (
                              <p className="text-xs text-blue-600">📁 {bucket.folderPath}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-lg font-bold ${getUsageTextColor(bucket.usagePercentage)}`}>
                            {bucket.monthlyDataTransferCap > 0 ? `${bucket.usagePercentage.toFixed(1)}%` : '∞'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {bucket.monthlyDataTransferCap > 0 
                              ? `${formatBytes(bucket.remainingBytes)} remaining`
                              : 'Unlimited'}
                          </p>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      {bucket.monthlyDataTransferCap > 0 && (
                        <div className="mb-3">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full transition-all ${getUsageColor(bucket.usagePercentage)}`}
                              style={{ width: `${Math.min(100, bucket.usagePercentage)}%` }}
                            ></div>
                          </div>
                        </div>
                      )}

                      {/* Stats Row */}
                      <div className="grid grid-cols-4 gap-2 text-center text-sm">
                        <div className="bg-gray-50 rounded p-2">
                          <p className="font-medium text-gray-900">{formatBytes(bucket.monthlyDataUsage)}</p>
                          <p className="text-xs text-gray-500">Used</p>
                        </div>
                        <div className="bg-green-50 rounded p-2">
                          <p className="font-medium text-green-700">{formatBytes(bucket.monthlyDownloadSize)}</p>
                          <p className="text-xs text-gray-500">↓ Down</p>
                        </div>
                        <div className="bg-blue-50 rounded p-2">
                          <p className="font-medium text-blue-700">{formatBytes(bucket.monthlyUploadSize)}</p>
                          <p className="text-xs text-gray-500">↑ Up</p>
                        </div>
                        <div className="bg-amber-50 rounded p-2">
                          <p className="font-medium text-amber-700">
                            {bucket.monthlyDataTransferCap > 0 ? `${bucket.monthlyDataTransferCap} GB` : '∞'}
                          </p>
                          <p className="text-xs text-gray-500">Cap</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200 bg-gray-50">
          <p className="text-xs text-gray-500">
            Data resets monthly on the 1st
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserDataUsageModal;
